const validators = {
    // Numeric Types
    smallint: (v) => Number.isInteger(Number(v)) && Number(v) >= -32768 && Number(v) <= 32767,
    integer: (v) => Number.isInteger(Number(v)) && Number(v) >= -2147483648 && Number(v) <= 2147483647,
    bigint: (v) => typeof v === 'bigint' || Number.isInteger(Number(v)),
    decimal: (v) => !isNaN(Number(v)),
    numeric: (v) => !isNaN(Number(v)),
    real: (v) => !isNaN(Number(v)),
    'double precision': (v) => !isNaN(Number(v)),
    serial: (v) => Number.isInteger(Number(v)) && Number(v) >= 1,
    bigserial: (v) => Number.isInteger(Number(v)) && Number(v) >= 1,

    // Character Types
    character: (v) => v !== null && v !== undefined,
    char: (v) => v !== null && v !== undefined,
    varchar: (v) => v !== null && v !== undefined,
    text: (v) => v !== null && v !== undefined,
    'character varying': (v) => typeof v !== 'object',

    // Boolean
    boolean: (v) => {
        if (typeof v === 'boolean') return true
        if (typeof v === 'number') return v === 0 || v === 1
        if (typeof v === 'string') return /^(true|false|yes|no|t|f|1|0)$/i.test(v.trim())
        return false
    },

    // Date/Time Types
    date: (v) => {
        if (v instanceof Date) return !isNaN(v.getTime())
        return !isNaN(Date.parse(String(v)))
    },
    time: (v) => /^\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(String(v)),
    'timestamp without time zone': (v) => {
        // ✅ Support format: YYYY-MM-DD HH:mm:ss
        if (v instanceof Date) return !isNaN(v.getTime())
        const s = String(v).trim()
        // Check format YYYY-MM-DD HH:mm:ss
        if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}$/.test(s)) return true
        // Also support other datetime formats
        return !isNaN(Date.parse(s))
    },
    timestamp: (v) => {
        if (v instanceof Date) return !isNaN(v.getTime())
        return !isNaN(Date.parse(String(v)))
    },
    'timestamp with time zone': (v) => {
        // ✅ Support format: YYYY-MM-DD HH:mm:ss
        if (v instanceof Date) return !isNaN(v.getTime())
        const s = String(v).trim()
        // Check format YYYY-MM-DD HH:mm:ss
        if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}$/.test(s)) return true
        // Also support other datetime formats with timezone
        return !isNaN(Date.parse(s))
    },
    timestamptz: (v) => {
        if (v instanceof Date) return !isNaN(v.getTime())
        const s = String(v)
        // Accept datetime with or without timezone indicator
        return !isNaN(Date.parse(s))
    },
    'time with time zone': (v) => /^\d{2}:\d{2}(:\d{2})?(\.\d+)?([Zz]|[+-]\d{2}:?\d{2})$/.test(String(v).trim()),
    interval: (v) => typeof v === 'string',

    // Binary
    bytea: (v) => v instanceof Uint8Array || v !== null && v !== undefined,

    // Network Types
    inet: (v) => {
        const s = String(v)
        const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/
        const ipv6 = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}(\/\d{1,3})?$/
        return ipv4.test(s) || ipv6.test(s)
    },
    cidr: (v) => /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/.test(String(v)),
    macaddr: (v) => /^([0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2}$/.test(String(v)),

    // JSON Types
    json: (v) => {
        if (typeof v === 'object') return v !== null
        if (Array.isArray(v)) return true
        try { JSON.parse(v); return true } catch { return false }
    },
    jsonb: (v) => {
        if (typeof v === 'object') return v !== null
        if (Array.isArray(v)) return true
        try { JSON.parse(v); return true } catch { return false }
    },

    // UUID
    uuid: (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v)),

    // Array
    array: (v) => Array.isArray(v),

    // XML
    xml: (v) => /^<[\s\S]*>$/.test(String(v).trim()),
}

// ─── Parsers ──────────────────────────────────────────────────────────────────

const parsers = {
    smallint: (v) => Number(v),
    integer: (v) => Number(v),
    bigint: (v) => typeof v === 'bigint' ? v : Number.isInteger(Number(v)) ? Number(v) : null,
    decimal: (v) => Number(v),
    numeric: (v) => Number(v),
    real: (v) => Number(v),
    'double precision': (v) => Number(v),
    serial: (v) => Number(v),
    bigserial: (v) => Number(v),

    character: (v) => String(v),
    char: (v) => String(v),
    varchar: (v) => String(v),
    text: (v) => String(v),
    'character varying': (v) => String(v),

    boolean: (v) => {
        if (typeof v === 'boolean') return v
        if (v === 1 || String(v).toLowerCase() === 'true' || /^(yes|t|1)$/i.test(String(v))) return true
        return false
    },

    date: (v) => v instanceof Date ? v : new Date(String(v)),
    time: (v) => String(v),
    'timestamp without time zone': (v) => {
        // ✅ Return string format YYYY-MM-DD HH:mm:ss
        if (typeof v === 'string') return v
        if (v instanceof Date) {
            // Convert Date to YYYY-MM-DD HH:mm:ss format
            const year = v.getFullYear()
            const month = String(v.getMonth() + 1).padStart(2, '0')
            const day = String(v.getDate()).padStart(2, '0')
            const hours = String(v.getHours()).padStart(2, '0')
            const minutes = String(v.getMinutes()).padStart(2, '0')
            const seconds = String(v.getSeconds()).padStart(2, '0')
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        }
        return String(v)
    },
    timestamp: (v) => v instanceof Date ? v : new Date(String(v)),
    'timestamp with time zone': (v) => {
        // ✅ Return string format YYYY-MM-DD HH:mm:ss untuk compatibility
        if (typeof v === 'string') return v
        if (v instanceof Date) {
            // Convert Date to YYYY-MM-DD HH:mm:ss format
            const year = v.getFullYear()
            const month = String(v.getMonth() + 1).padStart(2, '0')
            const day = String(v.getDate()).padStart(2, '0')
            const hours = String(v.getHours()).padStart(2, '0')
            const minutes = String(v.getMinutes()).padStart(2, '0')
            const seconds = String(v.getSeconds()).padStart(2, '0')
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        }
        return String(v)
    },
    timestamptz: (v) => String(v), // Keep as string to preserve timezone offset
    'time with time zone': (v) => String(v),
    interval: (v) => String(v),

    bytea: (v) => v instanceof Uint8Array ? v : new TextEncoder().encode(String(v)),

    inet: (v) => String(v),
    cidr: (v) => String(v),
    macaddr: (v) => String(v),

    json: (v) => typeof v === 'object' ? v : JSON.parse(v),
    jsonb: (v) => typeof v === 'object' ? v : JSON.parse(v),

    uuid: (v) => String(v).toLowerCase(),
    array: (v) => Array.from(v),
    xml: (v) => String(v),
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createValidator(pgType) {
    const normalizedInput = String(pgType).toLowerCase().trim().replace(/\s+/g, ' ')
    const baseType = normalizedInput.replace(/\(.+\)/, '').trim()

    const typeAliases = {
        bpchar: 'character',
        'varying character': 'character varying',
        'time without time zone': 'time',
        'time with time zone': 'time with time zone',
    }

    const normalizedType = typeAliases[baseType] || baseType
    const validatorFn = validators[normalizedType]
    const parserFn = parsers[normalizedType]

    if (!validatorFn) {
        throw new Error(`Unsupported PostgreSQL type: "${pgType}"`)
    }

    return {
        type: normalizedType,
        validate(value) {
            if (value === null || value === undefined) {
                return { valid: false, type: normalizedType, value, parsed: null, error: 'Value is null or undefined' }
            }

            const isValid = validatorFn(value)
            return {
                valid: isValid,
                type: normalizedType,
                value,
                parsed: isValid ? parserFn(value) : null,
                ...(isValid ? {} : { error: `Value "${value}" is not a valid ${normalizedType}` }),
            }
        },
    }
}